import { Button, Group, Radio } from '@mantine/core';
import {
  IconBrain,
  IconBuilding,
  IconBuildingStore,
  IconCar,
  IconWallet,
  IconArrowRight,
} from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { TitleDescriptionRadio } from 'src/Components/Shared/TitleDescriptionRadio';
import { CreateAssetDtoTypeEnum } from 'src/Services/API/generated';

import styles from './OnboardingTypeSelectForm.module.scss';
import { Props } from './OnboardingTypeSelectForm.types';

const OnboardingTypeSelectForm = ({ onTypeSelect, isFirstAsset }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [value, setValue] = useState<CreateAssetDtoTypeEnum>(CreateAssetDtoTypeEnum.RealEstate);

  const assetTypes = useMemo(
    () => [
      {
        title: t('onboarding.assetType.realEstate.title', 'Immobilier'),
        description: t(
          'onboarding.assetType.realEstate.description',
          'Résidences principales, secondaires...'
        ),
        icon: IconBuilding,
        key: CreateAssetDtoTypeEnum.RealEstate,
        disabled: false,
      },
      {
        title: t('onboarding.assetType.movable.title', 'Mobilier'),
        description: t(
          'onboarding.assetType.movable.description',
          "Une voiture, un bateau, une oeuvre d'art, ..."
        ),
        icon: IconCar,
        key: CreateAssetDtoTypeEnum.Movable,
        disabled: true,
      },
      {
        title: t('onboarding.assetType.financial.title', 'Financier'),
        description: t('onboarding.assetType.financial.description', 'Comptes, assurances, ...'),
        icon: IconWallet,
        key: CreateAssetDtoTypeEnum.Financial,
        disabled: true,
      },
      {
        title: t('onboarding.assetType.intellectual.title', 'Intellectuel'),
        description: t('onboarding.assetType.intellectual.description', 'Marques, brevets...'),
        icon: IconBrain,
        key: CreateAssetDtoTypeEnum.Intellectual,
        disabled: true,
      },
      {
        title: t('onboarding.assetType.business.title', 'Entreprises'),
        description: t('onboarding.assetType.business.description', 'Tous types et tailles'),
        icon: IconBuildingStore,
        key: CreateAssetDtoTypeEnum.Business,
        disabled: true,
      },
    ],
    [t]
  );

  return (
    <div className={styles.container}>
      <p className={styles.title}>
        {isFirstAsset
          ? t('onboarding.firstAssetTypeForm.title', 'Ajoutez votre premier bien')
          : t('onboarding.newAssetTypeForm.title', 'Ajoutez votre nouveau bien')}
      </p>
      <p className={styles.description}>
        {t('onboarding.assetTypeForm.subTitle', 'Quel type de bien voulez vous créer ?')}
      </p>

      <Radio.Group value={value} onChange={value => setValue(value as CreateAssetDtoTypeEnum)}>
        <div className={styles.grid}>
          {assetTypes.map(type => (
            <TitleDescriptionRadio
              key={type.key}
              value={type.key}
              illustrationIcon={type.icon}
              title={type.title}
              description={type.description}
              disabled={type.disabled}
              disabledLabel={
                type.disabled
                  ? t('onboarding.assetTypeForm.soonAvailable', 'Bientôt disponible')
                  : undefined
              }
            />
          ))}
        </div>
      </Radio.Group>
      <Group justify={!isFirstAsset ? 'space-between' : 'flex-end'} className={styles.actions}>
        {!isFirstAsset && (
          <Button variant={'outline'} onClick={() => navigate('/')}>
            {t('onboarding.cancel', 'Annuler')}
          </Button>
        )}
        <Button
          rightSection={<IconArrowRight color={'white'} height={18} />}
          onClick={() => onTypeSelect(value)}
        >
          {t('onboarding.next', 'Suivant')}
        </Button>
      </Group>
    </div>
  );
};

export default OnboardingTypeSelectForm;
